/**
 * CommandBar — AutoCAD-style command line under the CAD canvas.
 *
 *   L / LINE      line        PL / PLINE   polyline     T / TEXT   text
 *   M / MOVE      move        E / ERASE    delete       O / OFFSET offset
 *   DIM / DAL     dimension   I / INSERT   symbol       U / UNDO   undo edit
 *   LA <layer>    draw layer  GRID on|off  ORTHO on|off (F8)
 *   UNITS ftin|ft|m|mm        TS <mm>      dimension text size
 *   ESC           back to select;  Enter on empty = repeat last command
 * Typing a letter anywhere outside an input focuses the bar (like AutoCAD).
 */
import { useEffect, useRef, useState } from "react";
import { Terminal } from "lucide-react";
import { toast } from "sonner";

const TOOL_CMDS = {
  l: "cadline", line: "cadline",
  pl: "cadpoly", pline: "cadpoly", polyline: "cadpoly",
  t: "cadtext", text: "cadtext", dt: "cadtext",
  m: "cadmove", move: "cadmove",
  e: "caddelete", erase: "caddelete", del: "caddelete", delete: "caddelete",
  o: "cadoffset", offset: "cadoffset",
  dim: "caddim", dal: "caddim", dimaligned: "caddim", dimension: "caddim",
  i: "cadsymbol", insert: "cadsymbol", sym: "cadsymbol", symbol: "cadsymbol", stamp: "cadsymbol",
  sel: "select", select: "select", esc: "select",
};
const TOOL_LABEL = { cadline: "LINE — click two points", cadpoly: "PLINE — click points, Enter/double-click to finish", cadtext: "TEXT — click where the text goes", cadmove: "MOVE — pick an entity, then its new spot", caddelete: "ERASE — click the entity to delete", cadoffset: "OFFSET — pick an entity, then the side", caddim: "DIMALIGNED — click two points", cadsymbol: "INSERT — click to stamp the chosen symbol", select: "Select" };

export default function CommandBar({ tool, onTool, cadDraw, onCadDraw, layers = [], cadLayer, onCadLayer, onUndo, disabled }) {
  const [value, setValue] = useState("");
  const [history, setHistory] = useState([]);
  const [hIdx, setHIdx] = useState(-1);
  const [echo, setEcho] = useState("Command:");
  const inputRef = useRef(null);
  const lastCmd = useRef("");

  const say = (msg) => setEcho(msg);

  const run = (raw) => {
    const line = raw.trim();
    const text = line || lastCmd.current;
    if (!text) return;
    const [cmdRaw, ...rest] = text.split(/\s+/);
    const cmd = cmdRaw.toLowerCase();
    const arg = rest.join(" ");
    lastCmd.current = text;
    setHistory((h) => (h[0] === text ? h : [text, ...h].slice(0, 50)));
    setHIdx(-1);
    if (TOOL_CMDS[cmd]) {
      onTool(TOOL_CMDS[cmd]);
      say(`${cmdRaw.toUpperCase()}: ${TOOL_LABEL[TOOL_CMDS[cmd]]}`);
    } else if (cmd === "u" || cmd === "undo") {
      onUndo?.(); say("UNDO: last CAD edit removed");
    } else if (cmd === "la" || cmd === "layer") {
      if (!arg) { say(`LAYER: current draw layer is ${cadLayer}`); return; }
      const hit = layers.find((n) => n.toLowerCase() === arg.toLowerCase()) || layers.find((n) => n.toLowerCase().includes(arg.toLowerCase()));
      if (hit) { onCadLayer(hit); say(`LAYER: drawing on ${hit}`); } else { say(`LAYER: no layer matches "${arg}"`); toast.error(`No layer matches "${arg}"`); }
    } else if (cmd === "grid") {
      const on = arg ? !/^(off|0)$/i.test(arg) : !cadDraw?.grid;
      onCadDraw({ ...cadDraw, grid: on ? (Number(arg) > 0 ? Number(arg) : 10) : 0 }); say(`GRID: ${on ? "on" : "off"}`);
    } else if (cmd === "ortho" || cmd === "f8") {
      const on = arg ? !/^(off|0)$/i.test(arg) : !cadDraw?.ortho;
      onCadDraw({ ...cadDraw, ortho: on }); say(`ORTHO: ${on ? "on" : "off"}`);
    } else if (cmd === "units" || cmd === "dimunits") {
      const fmt = { ftin: "ftin", arch: "ftin", ft: "ft", dec: "ft", m: "m", mm: "mm" }[arg.toLowerCase()];
      if (fmt) { onCadDraw({ ...cadDraw, dimFormat: fmt }); say(`UNITS: dimensions in ${fmt}`); } else say("UNITS: ftin | ft | m | mm");
    } else if (cmd === "ts" || cmd === "textsize") {
      const h = Number(arg);
      if (h > 0) { onCadDraw({ ...cadDraw, dimHeight: h }); say(`TEXTSIZE: ${h} mm`); } else say("TEXTSIZE <mm>");
    } else if (cmd === "help" || cmd === "?") {
      say("L PL T M E O DIM I U · LA <layer> · GRID · ORTHO · UNITS ftin|ft|m|mm · TS <mm> · ESC");
    } else {
      say(`Unknown command "${cmdRaw}" — type ? for the list`);
    }
    setValue("");
  };

  // AutoCAD habit: start typing anywhere and it lands in the command line.
  useEffect(() => {
    if (disabled) return undefined;
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape") { onTool("select"); say("*Cancel*"); setValue(""); return; }
      if (e.key === "F8") { e.preventDefault(); run("ortho"); return; }
      if (/^[a-zA-Z0-9?]$/.test(e.key)) { inputRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line

  return (
    <div className="flex shrink-0 items-center gap-2 border-t border-white/10 bg-[#0b0d10] px-3 py-1.5 font-mono text-xs text-white" data-testid="cad-command-bar">
      <Terminal className="h-3.5 w-3.5 shrink-0 text-[#f97316]" />
      <span className="hidden max-w-[45%] truncate text-white/50 sm:inline" data-testid="cad-command-echo">{echo}</span>
      <span className="text-[#f97316]">{tool && tool !== "select" ? `[${(Object.keys(TOOL_CMDS).find((k) => TOOL_CMDS[k] === tool && k.length > 1) || tool).toUpperCase()}]` : ">"}</span>
      <input
        ref={inputRef}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); run(value); }
          else if (e.key === "Escape") { e.preventDefault(); setValue(""); onTool("select"); say("*Cancel*"); e.currentTarget.blur(); }
          else if (e.key === "ArrowUp") { e.preventDefault(); const i = Math.min(history.length - 1, hIdx + 1); setHIdx(i); setValue(history[i] || ""); }
          else if (e.key === "ArrowDown") { e.preventDefault(); const i = Math.max(-1, hIdx - 1); setHIdx(i); setValue(i < 0 ? "" : history[i]); }
        }}
        placeholder={disabled ? "Command line is available once the drawing is ready" : "Type a command — L, PL, DIM, O, M, LA <layer>, ? for help"}
        spellCheck={false}
        autoComplete="off"
        className="min-w-0 flex-1 bg-transparent text-white placeholder:text-white/30 focus:outline-none disabled:opacity-40"
        data-testid="cad-command-input"
      />
    </div>
  );
}
